// Learner-facing installment / EMI pages
//   /checkout/installment/:courseId    – plan selection + down payment
//   /app/student/installments          – list of my active plans
//   /app/student/installments/:id      – detail + pay-pending
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, CheckCircle2, Clock, AlertTriangle, PauseCircle, Zap,
  ArrowLeft, ArrowRight, Receipt, CreditCard, ShieldCheck,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// -------- STATUS BADGE ---------------------------------------------
function AccessBadge({ status }) {
  const map = {
    active:              { cls: "bg-emerald-50 text-emerald-700",  label: "Active",             icon: CheckCircle2 },
    grace_period:        { cls: "bg-amber-50 text-amber-700",      label: "Grace Period",       icon: AlertTriangle },
    paused:              { cls: "bg-rose-50 text-rose-700",        label: "Access Paused",      icon: PauseCircle },
    completed_payment:   { cls: "bg-blue-50 text-blue-700",        label: "Payment Complete",   icon: ShieldCheck },
    cancelled:           { cls: "bg-slate-100 text-slate-500",     label: "Cancelled",          icon: Clock },
  };
  const m = map[status] || map.cancelled;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${m.cls}`}>
      <Icon className="h-3 w-3" />{m.label}
    </span>
  );
}

// -------- RAZORPAY HELPER ------------------------------------------
async function payWithRazorpay({ order, razorpay_order_id, razorpay_key_id, test_mode, order_id, onSuccess, onFail }) {
  if (test_mode || !window.Razorpay || !razorpay_key_id || razorpay_key_id.includes("placeholder")) {
    // Test-mode fallback: call verify via test-pay endpoint
    try {
      await api.post(`/learner/checkout/test-pay/${order_id}`);
      onSuccess?.();
    } catch (e) { onFail?.(e); }
    return;
  }
  const rzp = new window.Razorpay({
    key: razorpay_key_id,
    amount: order.amount_paise,
    order_id: razorpay_order_id,
    name: "Steward Learning Cloud",
    description: `Installment for order ${order.order_number || ""}`,
    handler: async (resp) => {
      try {
        await api.post("/learner/checkout/verify-payment", {
          order_id, razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        onSuccess?.();
      } catch (e) { onFail?.(e); }
    },
  });
  rzp.open();
}


// ===================================================================
// 1. PLAN SELECTION PAGE  (/checkout/installment/:courseId)
// ===================================================================
export function InstallmentCheckoutPage() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState({
    billing_name: "", billing_email: "", billing_mobile: "",
    billing_address: "", billing_city: "", billing_state: "", billing_pincode: "",
  });

  useEffect(() => {
    if (!user) { nav(`/login?next=/checkout/installment/${courseId}`); return; }
    (async () => {
      try {
        const { data } = await api.get(`/learner/installment-options/${courseId}`);
        setOptions(data.data);
        if (data.data.installment_plans?.length) setSelected(data.data.installment_plans[0]);
        setBilling((b) => ({
          ...b,
          billing_name: user.full_name || "",
          billing_email: user.email || "",
          billing_mobile: user.mobile || "",
        }));
      } catch (err) { toast.error(formatApiError(err)); nav(`/courses`); }
    })();
  }, [courseId, user?.id]);

  const scheduleRows = useMemo(() => {
    if (!selected) return [];
    const rows = [{ n: 1, label: "Down Payment", amount: selected.down_payment_amount, offset: "today" }];
    for (let i = 2; i <= selected.number_of_installments; i++) {
      const days = selected.frequency === "quarterly" ? 90 * (i - 1)
                : selected.frequency === "custom" ? (selected.custom_interval_days || 30) * (i - 1)
                : 30 * (i - 1);
      rows.push({ n: i, label: `Installment ${i}`, amount: selected.installment_amount, offset: `+${days} days` });
    }
    return rows;
  }, [selected]);

  const submit = async () => {
    if (!selected) return toast.error("Select a plan");
    if (!terms) return toast.error("Please accept the terms");
    for (const k of ["billing_name", "billing_email", "billing_mobile", "billing_address", "billing_city", "billing_state", "billing_pincode"]) {
      if (!billing[k]) return toast.error(`Please fill ${k.replace("billing_", "")}`);
    }
    setBusy(true);
    try {
      const { data } = await api.post("/learner/checkout/select-installment-plan", {
        plan_id: selected.id, course_id: courseId, terms_accepted: true, ...billing,
      });
      await payWithRazorpay({
        ...data.data,
        order_id: data.data.order.id,
        onSuccess: () => {
          toast.success("Payment successful — access unlocked!");
          nav("/app/student/installments");
        },
        onFail: (e) => toast.error(formatApiError(e)),
      });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!options) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

  const noPlan = !options.installment_plans?.length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="installment-checkout">
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-12">
        <Link to={`/courses/${options.course.course_code?.toLowerCase()}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-3 w-3" /> Back to course
        </Link>
        <h1 className="mt-3 text-3xl font-black text-slate-900">Pay in easy installments.</h1>
        <p className="mt-1 text-sm text-slate-600">{options.course.course_title} · Course fee {inr(options.pricing?.final_price)}</p>

        {noPlan ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <Wallet className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm font-bold text-slate-900">Installment plans not available for this course.</p>
            <p className="mt-1 text-xs text-slate-500">Please pay the full amount or contact us.</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            {/* LEFT column — plan chooser + billing */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="installment-plans">
                <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Choose a plan</div>
                <div className="space-y-2">
                  {options.installment_plans.map((p) => {
                    const isSel = selected?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelected(p)}
                        data-testid={`installment-plan-${p.id}`}
                        className={`w-full rounded-xl border-2 p-4 text-left transition ${isSel ? "border-[#101876] bg-[#101876]/5" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-black text-slate-900">{p.plan_name}</div>
                            <div className="mt-0.5 text-xs text-slate-600">
                              Pay <span className="font-bold text-[#329112]">{inr(p.down_payment_amount)}</span> today,
                              then {p.number_of_installments - 1} × {inr(p.installment_amount)} · {p.frequency}
                            </div>
                          </div>
                          <div className={`h-4 w-4 rounded-full border-2 ${isSel ? "border-[#101876] bg-[#101876]" : "border-slate-300"}`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Billing details</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["billing_name", "Name"], ["billing_email", "Email"],
                    ["billing_mobile", "Mobile"], ["billing_pincode", "PIN Code"],
                    ["billing_city", "City"], ["billing_state", "State"],
                  ].map(([k, l]) => (
                    <label key={k} className="text-xs">
                      <span className="font-bold text-slate-700">{l}</span>
                      <input value={billing[k]} onChange={(e) => setBilling({ ...billing, [k]: e.target.value })}
                        data-testid={`installment-${k}`}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none" />
                    </label>
                  ))}
                  <label className="text-xs sm:col-span-2">
                    <span className="font-bold text-slate-700">Address</span>
                    <input value={billing.billing_address} onChange={(e) => setBilling({ ...billing, billing_address: e.target.value })}
                      data-testid="installment-billing_address"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none" />
                  </label>
                </div>
              </div>
            </div>

            {/* RIGHT column — schedule preview + pay button */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Payment schedule</div>
                <ul className="divide-y divide-slate-100" data-testid="installment-schedule-preview">
                  {scheduleRows.map((r) => (
                    <li key={r.n} className="flex items-center justify-between py-2.5 text-sm">
                      <div>
                        <div className="font-semibold text-slate-900">{r.label}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.offset}</div>
                      </div>
                      <div className="font-mono font-bold">{inr(r.amount)}</div>
                    </li>
                  ))}
                  <li className="flex items-center justify-between pt-3 text-sm font-black text-slate-900">
                    <div>Total</div>
                    <div className="font-mono">{inr(selected?.total_fee)}</div>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                <div className="mb-1 font-bold">Terms</div>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Course access may pause if installment is not paid after the grace period.</li>
                  <li>Certificate is available only after full payment.</li>
                </ul>
                <label className="mt-3 flex items-center gap-2 font-semibold text-slate-800">
                  <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="installment-accept-terms" />
                  I accept the terms
                </label>
              </div>

              <button onClick={submit} disabled={busy || !terms}
                className="w-full rounded-xl bg-[#329112] py-3.5 text-sm font-black text-white hover:bg-[#26720d] disabled:opacity-60"
                data-testid="installment-pay-btn">
                <Zap className="mr-2 inline h-4 w-4" />
                {busy ? "Processing…" : `Pay ${inr(selected?.down_payment_amount)} & unlock course`}
              </button>
              <p className="text-center text-[10px] text-slate-400">Secure payment via Razorpay · UPI, Cards, Net-banking</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ===================================================================
// 2. MY INSTALLMENTS LIST  (/app/student/installments)
// ===================================================================
export function MyInstallmentsPage() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/learner/my-installments");
      setRows(data.data?.enrollments || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  if (rows === null) return <DashboardLayout title="My Installments"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="My Installments" breadcrumbs={["Learning", "My Installments"]}>
      {rows.length === 0 ? (
        <EmptyState message="You don't have any active installment plans." />
      ) : (
        <ul className="space-y-3" data-testid="my-installments-list">
          {rows.map((e) => (
            <li key={e.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`enrol-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-black text-slate-900">{e.course?.course_title}</div>
                    <AccessBadge status={e.access_status} />
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{e.plan?.plan_name}</div>
                </div>
                <Link to={`/app/student/installments/${e.id}`}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid={`enrol-open-${e.id}`}>
                  View details <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <Stat label="Total" value={inr(e.total_amount)} />
                <Stat label="Paid" value={inr(e.amount_paid)} tone="green" />
                <Stat label="Pending" value={inr(e.pending_amount)} tone="amber" />
                <Stat label="Next due" value={e.next_due ? new Date(e.next_due.due_date).toLocaleDateString() : "—"} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`font-black ${cls}`}>{value}</div>
    </div>
  );
}


// ===================================================================
// 3. INSTALLMENT DETAIL  (/app/student/installments/:id)
// ===================================================================
export function MyInstallmentDetailPage() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/learner/my-installments/${id}`);
      setE(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [id]);

  const pay = async (sched) => {
    setBusyId(sched.id);
    try {
      const { data } = await api.post(`/learner/my-installments/${sched.id}/pay`);
      await payWithRazorpay({
        ...data.data,
        order_id: data.data.order.id,
        onSuccess: () => { toast.success("Installment paid!"); load(); },
        onFail: (err) => toast.error(formatApiError(err)),
      });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  if (!e) return <DashboardLayout title="Installment"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title={e.course?.course_title || "Installment"} breadcrumbs={["Learning", "My Installments"]}>
      <div className="space-y-6" data-testid="installment-detail">
        {/* Access status banner */}
        {e.access_status === "paused" && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5" data-testid="access-paused-banner">
            <div className="flex items-center gap-3">
              <PauseCircle className="h-6 w-6 text-rose-600" />
              <div>
                <div className="text-sm font-black text-rose-900">Your course access is temporarily paused.</div>
                <p className="mt-0.5 text-xs text-rose-700">
                  Please complete the pending installment payment below to resume learning. Your progress, notes and bookmarks are preserved.
                </p>
              </div>
            </div>
          </div>
        )}
        {e.access_status === "grace_period" && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900" data-testid="access-grace-banner">
            <strong>Grace period.</strong> Please clear the pending installment before the grace period expires to avoid access pause.
          </div>
        )}

        {/* Summary */}
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-4" data-testid="installment-summary">
          <Stat label="Course" value={e.course?.course_title || "-"} />
          <Stat label="Total fee" value={inr(e.total_amount)} />
          <Stat label="Paid" value={inr(e.amount_paid)} tone="green" />
          <Stat label="Pending" value={inr(e.pending_amount)} tone="amber" />
        </div>

        {/* Schedule table */}
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 p-4 text-sm font-black text-slate-900">Payment schedule</div>
          <ul className="divide-y divide-slate-100" data-testid="installment-schedule">
            {(e.schedules || []).map((s) => {
              const overdue = s.payment_status === "overdue";
              const paid = s.payment_status === "paid";
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`schedule-row-${s.installment_number}`}>
                  <div className="min-w-[120px]">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">#{s.installment_number}</div>
                    <div className="text-sm font-semibold text-slate-800">
                      Due {new Date(s.due_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[80px]">
                    <div className="font-mono text-sm font-bold">{inr(s.amount_due)}</div>
                  </div>
                  <div className="min-w-[110px]">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      paid ? "bg-emerald-50 text-emerald-700"
                      : overdue ? "bg-rose-50 text-rose-700"
                      : "bg-amber-50 text-amber-700"
                    }`}>{s.payment_status}</span>
                  </div>
                  <div>
                    {paid ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" />Paid</span>
                    ) : (
                      <button onClick={() => pay(s)} disabled={busyId === s.id}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#329112] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#26720d] disabled:opacity-60"
                        data-testid={`pay-schedule-${s.installment_number}`}>
                        <CreditCard className="h-3 w-3" />{busyId === s.id ? "Processing…" : "Pay now"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Certificate note */}
        {e.pending_amount > 0 && e.plan?.certificate_block_enabled && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600" data-testid="certificate-block-note">
            <Receipt className="mr-1 inline h-3 w-3" />
            Your certificate will be available after completion of pending payment.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
